import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { live } from "lit/directives/live.js";
import { when } from "lit/directives/when.js";

import { type CountryCode } from "libphonenumber-js";

import { emit } from "@/internals/events";
import { countriesData, otherCountry, type Country } from "@/internals/i18n/data";

import { isEmptyValue } from "@/helpers/validations";

import Inputmask from "@/vendors/inputmask-utils";

import type { SelectOption } from "@/components/form/atlas-select/types";
import type AtlasPhoneCountrySelect from "@/components/form/atlas-phone/atlas-phone-country-select";

import FormControl, { type FormControlProps } from "@/components/form/form-control";

import { getCountryInputInfo, brazilPhoneInfo } from "./phone-utils";

import type { CountryCodeWithOther, CountryInputInfo, MaskObject } from "./types";

import styles from "./atlas-phone.scss";

import "@/components/form/atlas-phone/atlas-phone-country-select";
import "@/components/form/atlas-option/atlas-option";

export type PhoneProps = FormControlProps & {
    "placeholder": string;
    "show-original-name": boolean;
    "highlight-countries": CountryCode[];
    "initial-country": CountryCode;
    "international": boolean;
};

/**
 * @dependency atlas-phone-country-select
 * @dependency atlas-option
 *
 * @event {CustomEvent} atlas-phone-country-change - Evento disparado quando o país selecionado é alterado
 * @event {CustomEvent} atlas-phone-focus - Evento disparado quando o input recebe foco
 * @event {CustomEvent} atlas-phone-blur - Evento disparado quando o input perde foco
 * @event {CustomEvent} atlas-phone-input - Evento disparado quando o usuário digita no input
 * @event {CustomEvent} atlas-phone-keydown - Evento disparado quando uma tecla é pressionada no input
 * @event {CustomEvent} atlas-phone-paste - Evento disparado quando o usuário cola algo no input
 *
 * @tag atlas-phone
 */
@customElement("atlas-phone")
export default class AtlasPhone extends FormControl {
    static styles = styles;

    /** A mensagem que aparecerá quando o input está vazio */
    @property({ type: String }) placeholder: string;

    /** Indica se o input deve mostrar o nome do país na língua mais falada do país */
    @property({ type: Boolean, attribute: "show-original-name" }) showOriginalName = false;

    /** Lista de códigos de países que serão destacados na lista de opções */
    @property({ type: Array, attribute: "highlight-countries" }) highlightCountries: CountryCode[] = [
        brazilPhoneInfo.countryCode
    ];

    /** O código do país que irá vir selecionado por padrão */
    @property({ type: String, attribute: "initial-country" }) initialCountry: CountryCode;

    /** Indica se o campo deve aceitar números internacionais */
    @property({ type: Boolean }) international = false;

    @state() private _selectedCountry: Country;

    @query("atlas-phone-country-select")
    private _countrySelect: AtlasPhoneCountrySelect;

    @query("input")
    private _input: HTMLInputElement;

    private _countryInputInfo: CountryInputInfo;

    private _currentMask: Inputmask.Instance;

    private _maskObject: MaskObject;

    constructor() {
        super();

        this.onSelectChange = this.onSelectChange.bind(this);
    }

    /** @internal */
    public connectedCallback() {
        super.connectedCallback?.();

        this._countryInputInfo = getCountryInputInfo();

        this.updateComplete.then(() => {
            this.setInitialCountry();
            this.setInputMask();
        });
    }

    /**
     * Seleciona um país pelo código ISO2 e atualiza o campo de telefone.
     * @param {string} iso2 O código ISO2 do país a ser selecionado
     */
    public selectCountryByIso2(iso2: string) {
        this._selectedCountry = this.getCountryByIso2(iso2);
        this._countrySelect.value = this._selectedCountry.iso2;
    }

    /**
     * Permite obter o país selecionado atualmente no campo de telefone.
     * @returns {Country} O país selecionado atualmente no campo de telefone
     */
    public getSelectedCountry(): Country {
        return this._selectedCountry;
    }

    /**
     * Retorna o valor do campo sem a máscara
     * @returns {string} - O valor do campo sem a máscara
     */
    public getUnmaskedValue(): string {
        return this._currentMask.unmaskedvalue();
    }

    private buildMaskObject() {
        const maskObject: MaskObject = {
            jitMasking: true,
            keepStatic: true,
            mask: brazilPhoneInfo.masks.national,
            regex: brazilPhoneInfo.regex,
            placeholder: brazilPhoneInfo.placeholder.national
        };

        if (this.international) {
            const countryCode = (this._selectedCountry?.iso2 || otherCountry.iso2) as CountryCodeWithOther;
            const selectedCountryInputInfo = this._countryInputInfo[countryCode];

            maskObject.mask = selectedCountryInputInfo.mask;
            maskObject.placeholder = selectedCountryInputInfo.placeholder;
            maskObject.regex = selectedCountryInputInfo.regex;
        }

        return maskObject;
    }

    private getPlaceholder() {
        if (this.placeholder) return this.placeholder;

        if (!isEmptyValue(this._maskObject)) {
            return this._maskObject.placeholder;
        }

        return "";
    }

    private setInputMask() {
        this._currentMask?.remove();

        this._maskObject = this.buildMaskObject();

        this._currentMask = new Inputmask(this._maskObject).mask(this._input);
    }

    private getCountryByIso2(iso2: string): Country {
        return countriesData.find((country) => country.iso2 === iso2) || otherCountry;
    }

    private setInitialCountry() {
        if (!this.international && !this.initialCountry) return;

        this.selectCountryByIso2(this.initialCountry);
    }

    private getOptionLabel(country: Country) {
        if (!this.showOriginalName) return country.name;

        return country.originalName === country.name ? country.name : `${country.name} (${country.originalName})`;
    }

    private onSelectChange() {
        const option = this._countrySelect.getSelectedOptions() as SelectOption;

        this._selectedCountry = this.getCountryByIso2(option.value);

        let dialCode = option.customProperties?.dialCode.split("-") || "";
        let areaCode;

        if (dialCode && dialCode.length > 1) {
            areaCode = dialCode[1];
        }

        dialCode = dialCode[0];

        this.setInputMask();

        this.value = `${dialCode}${areaCode ? ` ${areaCode}` : ""}`;

        emit(this, "atlas-phone-country-change", {
            detail: {
                selectedCountry: this._selectedCountry
            },
            trackDisable: true
        });
    }

    protected handleFocus() {
        emit(this, "atlas-phone-focus", { trackDisable: true });
    }

    protected handleBlur() {
        this.reportValidity();

        emit(this, "atlas-phone-blur", { trackDisable: true });
        emit(this, "atlas-form-element-touch", { trackDisable: true });
    }

    private handleInput() {
        this.value = this._input.value;

        emit(this, "atlas-phone-input", { detail: this._input.value, trackDisable: true });
    }

    private handleInputKeyDown(event: KeyboardEvent) {
        emit(this, "atlas-phone-keydown", { detail: { key: event.key }, trackDisable: true });
    }

    private handlePaste(event: ClipboardEvent) {
        emit(this, "atlas-phone-paste", {
            detail: { clipboardData: event.clipboardData },
            trackDisable: true
        });
    }

    private renderOtherOption() {
        return this.renderCountryOption(otherCountry);
    }

    private renderHighlitedCountries() {
        const highlightedCountries = countriesData.filter((country: Country) =>
            this.highlightCountries.includes(country.iso2 as CountryCode)
        );

        return highlightedCountries.map((country: Country) => {
            return this.renderCountryOption(country);
        });
    }

    private renderCountryOption(country: Country) {
        return html`
            <atlas-option
                value="${country.iso2}"
                label="${this.getOptionLabel(country)}"
                data-country-code="${country.iso2}"
                data-dial-code="${country.dialCode}"
                data-country-name="${country.name}"
            ></atlas-option>
        `;
    }

    private renderSelectCountry() {
        return when(
            this.international,
            () => html`
                <atlas-phone-country-select
                    enable-search
                    size="${this.size}"
                    class="atlas-select-input"
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                    @atlas-select-change=${this.onSelectChange}
                >
                    ${this.renderOtherOption()} ${this.renderHighlitedCountries()}
                    ${countriesData.map((country: Country) => {
                        return this.renderCountryOption(country);
                    })}
                </atlas-phone-country-select>
            `
        );
    }

    private renderInput() {
        const ariaLabel = this.disabled || this.readonly ? this.placeholder : "";
        const ariaDescribedBy = this.helperText ? `${this.id}-helper` : "";

        const formControlClass = {
            "form-control": true,
            [`form-control-${this.size}`]: !!this.size,
            [`is-${this._status}`]: this.getShowStatus(),
            "international": this.international,
            "readonly": this.readonly,
            "disabled": this.disabled
        };

        const inputClass = {
            "form-control-input": true,
            "readonly": this.readonly
        };

        return html`
            <div class=${classMap(formControlClass)}>
                <input
                    aria-describedby="${ariaDescribedBy}"
                    aria-label="${ariaLabel}"
                    id="${this.id}"
                    placeholder=${this.getPlaceholder()}
                    .value=${live(this.value)}
                    name="${this.name}"
                    class=${classMap(inputClass)}
                    type="tel"
                    inputmode="tel"
                    ?disabled=${this.disabled}
                    ?readonly=${this.readonly}
                    ?required=${this.required}
                    autocomplete="off"
                    @input=${this.handleInput}
                    @keydown=${this.handleInputKeyDown}
                    @paste=${this.handlePaste}
                    @focus=${this.handleFocus}
                    @blur=${this.handleBlur}
                />
            </div>
        `;
    }

    /**
     * @internal
     * @override
     */
    public render() {
        const inputGroupClass = {
            "skeleton": this.skeletonLoading,
            "input-group": true
        };

        return html`
            ${this.renderLabel()}
            <div class="input-wrapper">
                <div class=${classMap(inputGroupClass)} data-atlas-tooltip="input-tooltip">
                    ${this.renderSelectCountry()} ${this.renderInput()} ${this.renderStatusMessage()}
                </div>
            </div>
            ${this.renderHelperText({ isInputHelper: true })}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-phone": AtlasPhone;
    }
}
